import { type RefObject, useState } from 'react';
import { useSelector } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import { selectFormCorrelationId } from '@core/reducers/eventingSlice';
import convertToSlug from '@core/utils/strings/convertToSlug';

import SubmitButton from '../SubmitButton';
import { ContinueButton } from '../continueButtonsTypeDictionary';
import Popup from './Popup';
import styles from './PopupButton.module.css';

export type PopupButtonProps = {
  popupCtaLabel?: string;
  popupExitButtonName?: string;
  text?: string;
  heading?: string;
  popupContent?: string;
  continueButtonRef?: RefObject<HTMLButtonElement>;
  validateFields?: () => Promise<Error[]>;
};

const PopupButton: ContinueButton = ({
  popupCtaLabel = 'Agree & Get Matches',
  popupExitButtonName,
  text = 'See My Matches',
  heading = 'Terms and Conditions',
  popupContent = `<p>Your information is protected and will only be used by a school advisor to provide you with more details. By selecting <b>“See My Matches”</b>:</p><ul><li>You consent and agree to the following <a href='/terms-of-use/' target='_BLANK'>Terms of Use</a> and <a href='/privacy-policy/' target='_BLANK'>Privacy Policy</a></li><li>You agree that the programs you select may call and/or text you for marketing purposes regarding these programs at the telephone number you provided using an automated calling technology or an artificial or pre-recorded voice, even if you've registered that number on a Do-Not-Call registry.</li><li>You acknowledge that your consent to these communications is not a condition of doing business with us.</li></ul>`,
  onClick,
  disabled,
  continueButtonRef,
  validateFields,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  // Form Correlation Id for events
  const formCorrelationId = useSelector(selectFormCorrelationId);

  const { elementClicked } = useElementEvents({
    correlationId: formCorrelationId,
    webElement: { location: 'security-boost' },
  });

  const handleContinueClick = async () => {
    const errors = await validateFields?.();

    if (errors?.length) return;

    setShowPopup(true);

    const slug = convertToSlug(text);
    elementClicked({
      webElement: {
        htmlId: slug,
        name: slug,
      },
    });
  };

  const handleClose = () => {
    setShowPopup(false);
    elementClicked({
      webElement: {
        htmlId: popupExitButtonName,
        name: popupExitButtonName,
      },
    });
  };

  const handlePopupCtaClick = () => {
    onClick?.();
  };

  return (
    <>
      <Popup show={showPopup} onClose={handleClose}>
        <div className={styles.popupInnerContainer}>
          <h4 className={styles.popupHeading} dangerouslySetInnerHTML={{ __html: heading }} />
          <div className={styles.popupContent} dangerouslySetInnerHTML={{ __html: popupContent }} />

          <button onClick={handlePopupCtaClick} className={styles.popupActionButton} type="button">
            {popupCtaLabel}
          </button>
        </div>
      </Popup>

      <SubmitButton buttonRef={continueButtonRef} text={text} onClick={handleContinueClick} disabled={disabled} />
    </>
  );
};

export default PopupButton;
